/**
 * The Files view's orchestration layer — everything that drives the staged/sent
 * transfer, calling the API clients, body ops, and view but owning no transport or
 * rendering of its own. Three groups:
 *
 *   - Actions (user-initiated): send / re-attach / add / remove / cancel.
 *   - Upload: the client-driven chunked upload loop (register → chunk → confirm).
 *   - Lifecycle (on pane open): cleanup stale staged files, then resume a live
 *     transfer from the body if one is present, then arm the recipients watcher.
 *   - Recipients watcher: an Office.js RecipientsChanged handler retries an
 *     attach that failed for lack of a confirmed recipient, and after a send
 *     syncs To/Cc/Bcc edits to the recipient-gated transfer.
 *   - Attachment threshold: measures the draft's OWN attachments on open and on
 *     every AttachmentsChanged, and offers to move them into the transfer.
 *
 * The primary button does double duty: "Attach files securely" until a transfer
 * exists this session, then "Re-attach files securely" (queue consumed) so a
 * deleted link can be restored while the pane stays open.
 */

import {
  getAllRecipients,
  getSubject,
  getBodyHtml,
  getFileAttachments,
  getAttachmentFile,
  removeAttachment,
} from "../../js/utils/compose_item.js";
import { getConnection, getSessionId, getThreshold } from "../../js/utils/roaming_settings.js";
import { getQueue, clearQueue } from "../../js/utils/staged_queue.js";
import { SessionExpiredError } from "../../js/utils/session.js";
import {
  sendTransfer,
  addTransferFile,
  removeTransferFile,
  getTransferMetadata,
  renewTransfer,
  syncTransferRecipients,
  registerUpload,
  initChunk,
  uploadChunk,
  confirmUpload,
  deleteUpload,
} from "../../js/utils/api.js";
import {
  buildManifest,
  buildSecureLinkBlock,
  findSecureLinkOids,
  insertSecureLink,
  refreshSecureLink,
  discardOwnedTransfer,
  removeSecureLinksFromBody,
} from "./secure_link.js";
import { state, uid, persist } from "./files_state.js";
import {
  renderQueue,
  updateProgress,
  showSendStatus,
  clearSendStatus,
  updateSendButtonState,
  renderAttachmentBanner,
} from "./files_view.js";

// ---------------------------------------------------------------------------
// Actions — send
// ---------------------------------------------------------------------------

// Route the primary button. Once a transfer exists this session, new files are
// appended via "Add files" and removed with the row ✕ — the primary only
// re-inserts this session's link if the user deleted it from the body.
export async function onPrimaryAction() {
  if (state.lastAttached) {
    await reattachSecureLink();
    return;
  }
  await handleSend();
}

// Re-attach this session's already-created link (reuses the same transfer — no new
// upload, no new transfer). insertSecureLink replaces an existing block in place or
// drops a fresh one if the user deleted it.
async function reattachSecureLink() {
  if (state.sending) return;
  const item = Office.context.mailbox && Office.context.mailbox.item;
  if (!item || !state.lastAttached) return;
  try {
    await insertSecureLink(item, state.lastAttached.block, state.lastAttached.oid);
    showSendStatus("Secure link added to your email — now click Send in Outlook.", true);
  } catch (err) {
    console.warn("[Files] re-attach failed:", err);
    showSendStatus("Couldn't re-attach the secure link — please try again.", false);
  }
}

// Create + send the transfer from the staged uploads and insert the REAL secure
// link into the draft body, here in the pane. Works on every account and install
// channel (no dependency on a launch event). The user then clicks Outlook's own
// Send to mail it.
async function handleSend() {
  if (state.sending) return;
  const item = Office.context.mailbox && Office.context.mailbox.item;
  if (!item) return;
  awaitingRecipientsRetry = false; // any attempt consumes a pending recipients retry

  const midflight = state.queue.filter((e) => (e.status && e.status !== "uploaded") || !e.oid);
  if (midflight.length > 0) {
    showSendStatus(
      midflight.some((e) => e.status === "error")
        ? "Some uploads didn't finish. Retry or remove them, then send."
        : "Your files are still uploading. Wait a moment and try again.",
      false,
    );
    return;
  }
  if (state.queue.length === 0) {
    showSendStatus("Add files before sending.", false);
    return;
  }
  if (!getSessionId()) {
    showSendStatus("Connect to LeapFILE first — open the pane and click Connect.", false);
    return;
  }
  const recipients = await getAllRecipients(item);
  if (recipients.length === 0) {
    // Office.js only sees recipients Outlook has resolved into chips — an address
    // typed in To but never confirmed is invisible here. Arm a one-shot retry off
    // the RecipientsChanged watcher so confirming the address finishes the attach.
    awaitingRecipientsRetry = true;
    showSendStatus(
      "Add at least one recipient — if you've typed one, press Enter in the To field to confirm it and your files will attach automatically.",
      false,
    );
    return;
  }
  const subject = (await getSubject(item)).trim();
  if (!subject) {
    showSendStatus("Add a subject — LeapFILE uses it as the transfer name.", false);
    return;
  }
  const fileOids = state.queue.map((e) => e.oid);
  const sentQueue = state.queue.slice(); // snapshot for the manifest after we clear state

  state.sending = true;
  updateSendButtonState();
  showSendStatus("Securing your files…", true);
  try {
    const res = await sendTransfer({ subject, recipients, fileOids });
    if (!res.ok) {
      // Whitelist: only surface the server's message for 403s (the intentional,
      // user-actionable validations). Other statuses might leak internal detail.
      if (res.status !== 403 && res.error) {
        console.warn(`[Send] Suppressed ${res.status} server message: ${res.error}`);
      }
      showSendStatus(
        res.status === 403 && res.error
          ? res.error
          : "Couldn't secure your files. Please try again, or contact support.",
        false,
      );
      return;
    }

    const downloadLink = res.data && res.data.download_link;
    const newOid = res.data && res.data.transfer_oid;
    const availableUntil = (res.data && res.data.available_until) || "";
    // Map each staged upload to the content oid the server minted for it at send —
    // that's the id the remove endpoint needs to drop an originally-attached file.
    const contentByUpload = new Map(((res.data && res.data.files) || []).map((f) => [f.upload_oid, f.content_oid]));

    // The transfer is sent. Insert the real secure-link block (tagged with THIS
    // transfer's OID) into the body. handleSend only runs when no transfer exists
    // this session (appends go through Add files), so there's no stray block of
    // ours to replace, and we never touch links for other transfers (e.g. quoted
    // reply/forward history). If insert fails the transfer is still out, so tell
    // the user rather than claiming success.
    const block = buildSecureLinkBlock(downloadLink, buildManifest(sentQueue), availableUntil, newOid);
    let bodyInserted = true;
    try {
      await insertSecureLink(item, block, newOid);
    } catch (bodyErr) {
      bodyInserted = false;
      console.warn("[Send] Body insert failed after transfer was sent:", bodyErr);
    }
    // Keep the rendered block + its OID in memory so the user can re-attach it
    // (reusing this same transfer) if they delete the link while the pane stays
    // open, and the link + expiry so the card can be rebuilt when files change.
    // In-memory only — a reopen starts a fresh transfer.
    state.lastAttached = { block, oid: newOid, downloadLink, availableUntil };

    // Flip the files to "attached" rows — still visible and now editable (remove
    // with ✕, add more via Add files), each carrying its content oid. In-memory
    // display only; clear the sessionData staging queue via persist (attached rows
    // are excluded) so the reopen-wipe never deletes uploads that are now part of
    // the sent transfer.
    state.queue = state.queue.map((e) => ({ ...e, status: "attached", content_oid: contentByUpload.get(e.oid) }));
    state.files.clear();
    await persist();
    renderQueue();
    // Record the recipients the transfer was sent to; the watcher syncs later edits
    // to the transfer and uses this to skip a no-op sync.
    state.sentRecipients = recipients;
    showSendStatus(
      bodyInserted
        ? "Secure link added to your email — now click Send in Outlook."
        : "Files secured, but the link couldn't be added — please contact support.",
      bodyInserted,
    );
  } catch (err) {
    if (err instanceof SessionExpiredError) return; // guard is redirecting to connect
    console.error("[Send] failed:", err);
    showSendStatus("Couldn't secure your files. Please try again, or contact support.", false);
  } finally {
    state.sending = false;
    updateSendButtonState();
  }
}

// ---------------------------------------------------------------------------
// Actions — queue editing
// ---------------------------------------------------------------------------

export async function addFiles(picked) {
  if (!picked.length) return;
  // When a transfer already exists this session, added files are appended to it
  // (upload, then attach as content) rather than staged for a fresh send.
  const appending = !!state.lastAttached;
  clearSendStatus();
  const added = [];
  for (const file of picked) {
    const entry = { id: uid(), name: file.name, size: file.size, status: "queued", percent: 0 };
    state.files.set(entry.id, file);
    state.queue.push(entry);
    added.push(entry.id);
  }
  renderQueue();
  // Upload one at a time so "Uploading" reflects the file actually in flight;
  // when appending, attach each to the sent transfer as soon as it's uploaded.
  for (const id of added) {
    await uploadEntry(id);
    if (appending) await attachToTransfer(id);
  }
}

// Attach a freshly-uploaded file to this session's already-sent transfer, turning
// it into an "attached" row carrying the content oid the server returns (so it can
// be removed later). Only meaningful in the append flow (a transfer exists). On
// failure the row is left errored with an actionable message.
async function attachToTransfer(id) {
  const entry = state.queue.find((e) => e.id === id);
  if (!entry || entry.status !== "uploaded" || !entry.oid || !state.lastAttached) return;
  try {
    const res = await addTransferFile(state.lastAttached.oid, entry.oid);
    if (!res.ok) {
      entry.status = "error";
      entry.error = res.status === 403 && res.error ? res.error : "Couldn't add the file to your transfer.";
      renderQueue();
      return;
    }
    entry.status = "attached";
    entry.content_oid = res.data && res.data.content_oid;
    state.files.delete(id);
    await persist(); // attached files leave the staging queue (they're on the transfer now)
    renderQueue();
    await refreshSecureLink(); // keep the card's file summary in sync with the new file
    showSendStatus("File added to your secure link.", true);
  } catch (err) {
    if (err instanceof SessionExpiredError) return; // guard is redirecting to connect
    console.warn("[Files] attach to transfer failed:", err);
    entry.status = "error";
    entry.error = "Couldn't add the file to your transfer.";
    renderQueue();
  }
}

export async function removeEntry(id) {
  const entry = state.queue.find((e) => e.id === id);
  if (!entry) return;
  clearSendStatus();

  // Attached rows live on the sent transfer — remove them there (the server
  // refuses to drop the last file). Keep the row if the removal is rejected.
  if (entry.status === "attached") {
    if (!state.lastAttached || !entry.content_oid) return;
    try {
      const res = await removeTransferFile(state.lastAttached.oid, entry.content_oid);
      if (!res.ok) {
        showSendStatus(
          res.status === 403 && res.error ? res.error : "Couldn't remove the file. Please try again.",
          false,
        );
        return;
      }
    } catch (err) {
      if (err instanceof SessionExpiredError) return; // guard is redirecting to connect
      console.warn("[Files] remove attached file failed:", err);
      showSendStatus("Couldn't remove the file. Please try again.", false);
      return;
    }
    state.queue = state.queue.filter((e) => e.id !== id);
    renderQueue();
    await refreshSecureLink(); // keep the card's file summary in sync with the removal
    return;
  }

  // Staged (not-yet-sent) upload — delete the staged upload (fire-and-forget).
  if (entry.oid) deleteUpload(entry.oid).catch(() => {});
  state.files.delete(id);
  state.queue = state.queue.filter((e) => e.id !== id);
  await persist();
  renderQueue();
}

// ---------------------------------------------------------------------------
// Actions — cancel
// ---------------------------------------------------------------------------

export async function handleCancel() {
  // Delete every staged (not-yet-sent) upload. Attached rows belong to a sent
  // transfer — those are cleaned up by deleting the transfer below, not here.
  const oids = state.queue.filter((e) => e.status !== "attached").map((e) => e.oid).filter(Boolean);
  await Promise.all(oids.map((oid) => deleteUpload(oid).catch(() => {})));

  // If this session created a transfer, strip ITS link from the body (by OID) and
  // delete it — Cancel discards the work, so it shouldn't leave a live link (or its
  // undelivered transfer) behind. Scoped to our OID: a link quoted from an earlier
  // message in a reply/forward draft is left untouched.
  if (state.lastAttached) {
    const item = Office.context.mailbox && Office.context.mailbox.item;
    if (item) await discardOwnedTransfer(item, state.lastAttached.oid);
  }

  state.queue = [];
  state.files.clear();
  state.sentRecipients = null;
  state.lastAttached = null;
  awaitingRecipientsRetry = false;
  if (recipientsRetryTimer) clearTimeout(recipientsRetryTimer);
  recipientsRetryTimer = null;
  await persist();
  renderQueue();

  try {
    Office.context.ui.closeContainer();
  } catch (err) {
    console.warn("[Files] closeContainer failed:", err);
  }
}

// ---------------------------------------------------------------------------
// Upload — client-driven chunked upload
// ---------------------------------------------------------------------------

// register → loop(init → upload chunk) → confirm. Each chunk request only resolves
// once the chunk is relayed to S3, so per-file progress reflects real end-to-end
// upload (accurate on localhost and over the network). The endpoint calls live in
// js/utils/api.js; this drives the loop, updates the queue entry, and repaints.
async function uploadEntry(id) {
  const entry = state.queue.find((e) => e.id === id);
  const file = state.files.get(id);
  if (!entry || !file) return;

  entry.status = "uploading";
  entry.percent = 0;
  entry.error = undefined;
  renderQueue();

  let expired = false;
  try {
    const reg = await registerUpload(file);
    if (!reg.ok || !reg.data || !reg.data.oid) throw new Error(reg.error || "Could not start the upload.");
    entry.oid = reg.data.oid;

    const total = file.size || 1;
    while (true) {
      const init = await initChunk(entry.oid);
      if (!init.ok || !init.data) throw new Error(init.error || "Upload failed.");
      const info = init.data; // { chunk_id, start?, end? }
      if (info.chunk_id === "FIN") break;
      if (info.chunk_id === "PROCESS") {
        await new Promise((r) => setTimeout(r, 100));
        continue;
      }
      const bytes = await file.slice(info.start, info.end + 1).arrayBuffer();
      const put = await uploadChunk(entry.oid, info.chunk_id, bytes);
      if (!put.ok) throw new Error(put.error || "Upload failed.");
      updateProgress(id, Math.min(99, Math.round(((info.end + 1) / total) * 100)));
    }

    const done = await confirmUpload(entry.oid);
    if (!done.ok) throw new Error(done.error || "Upload failed.");
    entry.percent = 100;
    entry.status = "uploaded";
  } catch (err) {
    // Session expired mid-upload: the guard is clearing roaming state and
    // navigating to connect, so don't render a dead-end "Session expired" row.
    if (err instanceof SessionExpiredError) {
      expired = true;
      return;
    }
    entry.status = "error";
    entry.error = (err && err.message) || "Upload failed";
  } finally {
    state.files.delete(id);
    if (!expired) {
      await persist();
      renderQueue();
    }
  }
}

// ---------------------------------------------------------------------------
// Lifecycle — pane open
// ---------------------------------------------------------------------------

export async function onFilesViewMounted() {
  // Fill the account footer's "Signed in as" label from the stored connection
  // (the view is server-rendered, so the username isn't known at render time).
  populateSignedInAs();
  // Wipe this draft's own staged (not-yet-sent) files + in-memory state so every
  // open starts clean. sessionData is per-compose, so this only touches THIS draft.
  await cleanupOnOpen();
  // Then, if the body still carries a live transfer this user owns, resume it —
  // rehydrate the attached files + recipient snapshot so editing continues instead
  // of starting over. cleanupOnOpen left the body untouched, so this only reads it.
  await resumeFromBody();
  // Watch for recipient edits after a send (the link is recipient-gated).
  armRecipientsWatcher();
  // Then the large-attachment suggestion: watch for attachment edits and measure
  // what the draft is already carrying against the account's threshold.
  armAttachmentsWatcher();
  await checkAttachmentThreshold();
}

// Fill the account footer's email label from the stored connection (its
// `username` is the account email). The view is server-rendered with the label
// hidden; reveal it only once we have an email, so it never shows an empty label.
function populateSignedInAs() {
  const label = document.querySelector("[data-oa-signed-in-label]");
  const value = label && label.querySelector("[data-oa-signed-in-as]");
  if (!label || !value) return;
  const email = getConnection()?.username || "";
  value.textContent = email;
  value.title = email;
  label.classList.toggle("hidden", !email);
}

// Wipe-on-open: every pane open starts with a clean file list — this draft's
// previously-staged files are discarded (delete the server uploads, clear the
// queue) and not restored. sessionData is per-compose, so no other open draft is
// touched. Orphaned uploads from drafts that are abandoned without sending are
// reaped server-side, not here (this draft's sessionData is gone once it closes).
async function cleanupOnOpen() {
  const session = getSessionId();
  const oids = (await getQueue()).map((e) => e.oid).filter(Boolean);

  // Paint an empty list immediately; the server deletes are best-effort below.
  state.queue = [];
  state.files.clear();
  state.sentRecipients = null; // fresh open — no send to watch yet
  state.lastAttached = null; // fresh open — nothing to re-attach
  state.oversizeAttachments = []; // fresh open — the banner is re-measured below
  state.attachmentPromptDismissed = false; // fresh open — the user can be asked again
  state.movingAttachments = false;
  awaitingRecipientsRetry = false; // fresh open — no attach waiting on a recipient
  if (recipientsRetryTimer) clearTimeout(recipientsRetryTimer);
  recipientsRetryTimer = null;
  renderQueue();
  await clearQueue();

  if (!session || !oids.length) return;
  // Serial, best-effort: a failed delete shouldn't stop the rest.
  for (const oid of oids) {
    try {
      await deleteUpload(oid);
    } catch {
      /* ignore — move on to the next */
    }
  }
}

// Resume this draft's transfer from the secure link already in the body, so a
// reopened draft keeps editing the same transfer. Reads the block's OID, asks the
// server whether it's still a live transfer this user owns, and if so rehydrates
// the attached-file rows + recipient snapshot + re-attach state. Only resumes when
// EXACTLY ONE body link is a live owned transfer — with several (e.g. quoted
// reply/forward history) there's no way to tell which is the active one, so we
// leave them untouched and start fresh. Best-effort; any failure just starts fresh.
async function resumeFromBody() {
  const item = Office.context.mailbox && Office.context.mailbox.item;
  const session = getSessionId();
  if (!item || !session) return;

  let body;
  try {
    body = await getBodyHtml(item);
  } catch (err) {
    console.warn("[Files] resume: could not read body:", err);
    return;
  }
  const oids = findSecureLinkOids(body);
  if (oids.length === 0) return;

  // Ask the server about each of the body's links. A live transfer this user owns
  // is resumable; one that's ours but expired is a dead card to clean up (a 404 —
  // not owned / deleted — we leave alone, since we can't prove it's ours).
  const resumable = [];
  const expiredOids = [];
  for (const oid of oids) {
    try {
      const res = await getTransferMetadata(oid);
      if (!res.ok) continue; // 404 = deleted / not owned, etc. — leave it in the body
      if (res.data && res.data.editable) resumable.push(res.data);
      else if (res.data && res.data.expired) expiredOids.push(oid); // ours, but dead
    } catch (err) {
      if (err instanceof SessionExpiredError) return; // guard is redirecting to connect
      console.warn("[Files] resume: metadata lookup failed for", oid, err);
    }
  }

  // Strip any of the user's own expired links from the body — a dead download
  // button must never ride out to recipients, and it would otherwise sit next to a
  // freshly created card. Scoped to owned+expired OIDs; body only (no server delete
  // — the transfer is already dead). Runs whether or not we go on to resume.
  if (expiredOids.length > 0) await removeSecureLinksFromBody(item, expiredOids);

  if (resumable.length !== 1) {
    // Nothing live to resume. If we just cleared an expired link, say why the pane
    // is empty so a weeks-old draft doesn't look broken.
    if (expiredOids.length > 0) {
      showSendStatus("Your previous secure link expired — add your files again to send a new one.", false);
    }
    return;
  }

  const meta = resumable[0];
  const downloadLink = meta.download_link;
  if (!downloadLink) return; // can't rebuild the card without the host — start fresh

  // Top the delivery window back up. The expiration was set when the transfer was
  // created (Attach files securely); a draft parked for days would otherwise
  // deliver a shortened window. Extend-only server-side — an unchanged (fresh)
  // window comes back the same. Best-effort: on failure we keep the stored date.
  let availableUntil = meta.available_until || "";
  let windowExtended = false;
  try {
    const renewed = await renewTransfer(meta.transfer_oid);
    if (renewed.ok && renewed.data) {
      windowExtended = !!renewed.data.extended;
      if (renewed.data.available_until) availableUntil = renewed.data.available_until;
    }
  } catch (err) {
    if (err instanceof SessionExpiredError) return; // guard is redirecting to connect
    console.warn("[Files] resume: renew failed:", err);
  }

  // Rehydrate the attached rows from the transfer's files (content oids let them be
  // removed; sizes come back from the server so the card totals stay right).
  state.queue = (meta.files || []).map((f) => ({
    id: uid(),
    name: f.name || "",
    size: f.size || 0,
    status: "attached",
    content_oid: f.content_oid,
  }));
  state.files.clear();
  // Snapshot the recipients the transfer already has so the watcher only syncs real
  // changes made after this reopen.
  state.sentRecipients = (meta.recipients || []).map((e) => (e || "").toLowerCase());
  state.lastAttached = {
    oid: meta.transfer_oid,
    downloadLink,
    availableUntil,
    block: buildSecureLinkBlock(downloadLink, buildManifest(state.queue), availableUntil, meta.transfer_oid),
  };
  // If the window was actually extended, reinsert the rebuilt card so the body's
  // "available until" footer matches — but only then (avoid a needless body write
  // on every reopen), and only if the link is still in the body (best-effort).
  if (windowExtended) {
    try {
      const current = await getBodyHtml(item);
      if (findSecureLinkOids(current).includes(meta.transfer_oid)) {
        await insertSecureLink(item, state.lastAttached.block, meta.transfer_oid);
      }
    } catch (err) {
      console.warn("[Files] resume: card refresh failed:", err);
    }
  }
  renderQueue();
  showSendStatus("Resumed your secure link — edit files or recipients, then click Send in Outlook.", true);
}

// ---------------------------------------------------------------------------
// Recipients watcher
// ---------------------------------------------------------------------------

// The secure link is recipient-gated, so after a send any To/Cc/Bcc edit must be
// reflected on the transfer's receivers. Uses the Office.js RecipientsChanged event
// (NOT a launch event — it fires while the pane is open, on every install channel),
// debounces bursts, and syncs the delta to the same transfer.

// Guard so the RecipientsChanged handler is registered at most once per pane load.
let recipientsWatcherArmed = false;
// Debounce handle for syncing recipient edits to the sent transfer.
let recipientsSyncTimer = null;
// True after an attach failed for lack of committed recipients — the next
// RecipientsChanged (the user confirming the typed address) re-runs the attach.
let awaitingRecipientsRetry = false;
// Debounce handle for that retry, so a burst of chips settles before we send.
let recipientsRetryTimer = null;

// Fallback shown only if auto-sync of recipients fails (e.g. plan limit).
const RECIPIENT_DRIFT_MSG =
  "Couldn't update the recipients on your secure link. " +
  "Remove the new recipient, or click Start transfer to create a fresh link.";

// Register the watcher once per pane load; harmless if the host can't register it
// (read surfaces) — we just never get the callback.
function armRecipientsWatcher() {
  if (recipientsWatcherArmed) return;
  const item = Office.context.mailbox && Office.context.mailbox.item;
  if (!item || typeof item.addHandlerAsync !== "function") return;
  recipientsWatcherArmed = true;
  item.addHandlerAsync(Office.EventType.RecipientsChanged, onRecipientsChanged, (res) => {
    if (res.status !== Office.AsyncResultStatus.Succeeded) {
      recipientsWatcherArmed = false; // let a later mount retry
    }
  });
}

// Fires on any To/Cc/Bcc change. Before a send it only matters while an attach
// is waiting on a confirmed recipient — then it re-runs the attach. After a send,
// debounce rapid edits and sync the transfer's receivers to the email's current
// recipients.
function onRecipientsChanged() {
  if (awaitingRecipientsRetry && !state.lastAttached) {
    if (recipientsRetryTimer) clearTimeout(recipientsRetryTimer);
    recipientsRetryTimer = setTimeout(() => {
      if (awaitingRecipientsRetry && !state.lastAttached) handleSend();
    }, 400);
    return;
  }
  if (!state.lastAttached || !state.lastAttached.oid) return;
  if (recipientsSyncTimer) clearTimeout(recipientsSyncTimer);
  recipientsSyncTimer = setTimeout(syncRecipients, 800);
}

// Sync the sent transfer's receivers to the email's current recipients, so a
// recipient added/removed after attaching is reflected on the recipient-gated
// link. Reuses the same transfer (no new one). On failure, fall back to the manual
// guidance so the user can fix it via Start transfer.
async function syncRecipients() {
  const item = Office.context.mailbox && Office.context.mailbox.item;
  const session = getSessionId();
  if (!item || !session || !state.lastAttached || !state.lastAttached.oid) return;
  const recipients = await getAllRecipients(item);
  if (recipients.length === 0) return; // nothing to sync yet (no recipients on the draft)
  // Skip if the set is unchanged since the last sync — RecipientsChanged can fire
  // for edits that don't alter the recipient set. (Both are lowercased + deduped.)
  if (
    state.sentRecipients &&
    state.sentRecipients.length === recipients.length &&
    recipients.every((e) => state.sentRecipients.includes(e))
  ) {
    return;
  }
  showSendStatus("Updating recipients…", true);
  try {
    const res = await syncTransferRecipients(state.lastAttached.oid, recipients);
    if (!res.ok) {
      // Surface the server's message for 403 (e.g. plan recipient limit); otherwise
      // the generic fallback. Either way the user can redo via Start transfer.
      showSendStatus(res.status === 403 && res.error ? res.error : RECIPIENT_DRIFT_MSG, false);
      return;
    }
    state.sentRecipients = (res.data && res.data.recipients) || recipients;
    showSendStatus("Recipients updated — your secure link is in sync.", true);
  } catch (err) {
    if (err instanceof SessionExpiredError) return; // guard is redirecting
    console.warn("[Files] recipient sync failed:", err);
    showSendStatus(RECIPIENT_DRIFT_MSG, false);
  }
}

// ---------------------------------------------------------------------------
// Attachment threshold
// ---------------------------------------------------------------------------

// The web equivalent of the classic plugin's "Use threshold": once the draft's own
// attachments total more than the account's limit, offer to move them into a secure
// link instead. The limit is set in the main app (Account > Desktop client
// management > Outlook settings) and only read here — the pane never writes it.
// Like the classic plugin this measures the attachments TOGETHER, not one by one.
// It runs on pane open and on every AttachmentsChanged — a version that
// fired with the pane closed would need the OnMessageAttachmentsChanged launch
// event, which Microsoft only activates for admin-deployed add-ins (the same wall
// that removed OnMessageSend), so the marketplace install would never see it.

// Guard so the AttachmentsChanged handler is registered at most once per pane load.
let attachmentsWatcherArmed = false;
// Debounce handle — attaching several files fires one event per file.
let attachmentsCheckTimer = null;

export async function checkAttachmentThreshold() {
  const item = Office.context.mailbox && Office.context.mailbox.item;
  const threshold = getThreshold();
  if (!item || !threshold.enabled || state.attachmentPromptDismissed || state.movingAttachments) {
    state.oversizeAttachments = [];
    renderAttachmentBanner();
    return;
  }
  const attachments = await getFileAttachments(item);
  const total = attachments.reduce((sum, a) => sum + (a.size || 0), 0);
  state.oversizeAttachments = total > threshold.mb * 1024 * 1024 ? attachments : [];
  renderAttachmentBanner();
}

// Move the offered attachments into the pane's queue: read each one's bytes, upload
// it, and only then drop it from the email — a failed upload must never lose the
// user's file. Serial by design: Outlook allows only three concurrent async calls,
// and the upload loop handles one file at a time anyway.
export async function moveAttachmentsToLeapfile() {
  const item = Office.context.mailbox && Office.context.mailbox.item;
  if (!item || state.movingAttachments || state.oversizeAttachments.length === 0) return;
  const attachments = state.oversizeAttachments;
  state.movingAttachments = true;
  state.oversizeAttachments = [];
  renderAttachmentBanner();
  updateSendButtonState(); // the queue is incomplete until every file is moved
  clearSendStatus();

  // Seed a row per attachment BEFORE reading any bytes: Outlook hands a large
  // attachment back over seconds and renders nothing of its own, so building the
  // rows as each file arrives would leave the pane blank meanwhile. Name and size
  // come from the attachment details, which we already have. The rows wait as
  // "queued" — the same state a picked file sits in — and each turns to "moving"
  // only while it's the one being read.
  const appending = !!state.lastAttached;
  const rows = attachments.map((details) => {
    const entry = { id: uid(), name: details.name, size: details.size || 0, status: "queued", percent: 0 };
    state.queue.push(entry);
    return { details, entry };
  });
  renderQueue();

  const failed = [];
  try {
    for (const { details, entry } of rows) {
      entry.status = "moving";
      renderQueue();
      try {
        state.files.set(entry.id, await getAttachmentFile(item, details));
      } catch (err) {
        console.warn("[Files] could not read attachment", details.name, err);
        entry.status = "error";
        entry.error = "Couldn't read this file from your email.";
        renderQueue();
        failed.push(details.name);
        continue;
      }
      await uploadEntry(entry.id);
      if (appending) await attachToTransfer(entry.id);
      if (entry.status !== "uploaded" && entry.status !== "attached") {
        failed.push(details.name);
        continue;
      }
      try {
        await removeAttachment(item, details.id);
      } catch (err) {
        // The copy is safely uploaded, so the file isn't lost — it's just in the
        // email twice until the user deletes the original.
        console.warn("[Files] could not remove attachment", details.name, err);
        failed.push(details.name);
      }
    }
  } finally {
    state.movingAttachments = false;
    updateSendButtonState();
  }
  // Only a failure silences the banner: whatever couldn't be moved is still on the
  // email, and re-offering it every time would just nag. After a clean move a new
  // oversized attachment is measured again.
  state.attachmentPromptDismissed = failed.length > 0;

  showSendStatus(
    failed.length
      ? `Couldn't move ${failed.join(", ")} — delete ${failed.length > 1 ? "them" : "it"} from the email and add ${failed.length > 1 ? "them" : "it"} here instead.`
      : state.lastAttached
        ? "Moved into your secure link."
        : "Moved into LeapFILE — now click Attach files securely.",
    failed.length === 0,
  );
}

// "Not now": drop the offer for the rest of this pane-open. A fresh open asks again.
export function dismissAttachmentPrompt() {
  state.attachmentPromptDismissed = true;
  state.oversizeAttachments = [];
  renderAttachmentBanner();
}

// Register the watcher once per pane load; harmless if the host can't register it —
// we just never get the callback and the on-open measurement stands.
function armAttachmentsWatcher() {
  if (attachmentsWatcherArmed) return;
  const item = Office.context.mailbox && Office.context.mailbox.item;
  if (!item || typeof item.addHandlerAsync !== "function") return;
  attachmentsWatcherArmed = true;
  item.addHandlerAsync(Office.EventType.AttachmentsChanged, onAttachmentsChanged, (res) => {
    if (res.status !== Office.AsyncResultStatus.Succeeded) {
      attachmentsWatcherArmed = false; // let a later mount retry
    }
  });
}

function onAttachmentsChanged() {
  if (attachmentsCheckTimer) clearTimeout(attachmentsCheckTimer);
  attachmentsCheckTimer = setTimeout(checkAttachmentThreshold, 400);
}
